import type { FilterPill } from '@/tableControls'

export type AmountFilterOp = 'eq' | 'gt' | 'gte' | 'lt' | 'lte'

export interface DocReviewFilters {
  vendor: string
  doc_type: string
  location: string
  source_type: string
  reference_number: string
  date_from: string
  date_to: string
  amount_op: AmountFilterOp
  amount: string
  flags: string
}

export const DOC_REVIEW_EMPTY_FILTERS: DocReviewFilters = {
  vendor: '',
  doc_type: '',
  location: '',
  source_type: '',
  reference_number: '',
  date_from: '',
  date_to: '',
  amount_op: 'eq',
  amount: '',
  flags: '',
}

const FIELD_LABELS: Record<
  keyof Omit<DocReviewFilters, 'amount_op'>,
  string
> = {
  vendor: 'Vendor',
  doc_type: 'Type',
  location: 'Location',
  source_type: 'Source',
  reference_number: 'Doc Ref #',
  date_from: 'From (Issue Date)',
  date_to: 'To (Issue Date)',
  amount: 'Amount',
  flags: 'Flags',
}

const DOC_TYPE_LABELS: Record<string, string> = {
  bill: 'Bill',
  vendor_credit: 'Credit',
  card_expense: 'Card expense',
  card_credit: 'Card credit',
}

const SOURCE_LABELS: Record<string, string> = {
  email: 'Email',
  upload: 'Upload',
  drive_scan: 'Scan',
  amazon_report: 'Amazon',
}

const FLAG_LABELS: Record<string, string> = {
  duplicate: 'Duplicate in pending',
  duplicate_approved: 'Duplicate in approved',
  qbo_duplicate: 'Duplicate in QBO',
  missing_categories: 'Missing categories',
  missing_doc_type: 'Missing doc type',
  missing_location: 'Missing location',
  missing_unknown_vendor: 'Missing/unknown vendor',
  missing_bill_dates: 'Missing bill issue or due date',
  missing_line_items: 'No line items (bill or vendor credit)',
  non_payable_notice: 'Not an invoice / do not pay',
  total_mismatch: 'Total mismatch',
  shared_costs: 'Shared costs (tax, shipping, discount, tip, fees, …)',
  enrichment_pending: 'COGS enrichment pending',
  enrichment_needs_review: 'COGS enrichment needs review',
  enrichment_failed: 'COGS enrichment failed',
  pre_delivery: 'Pre-delivery invoice',
  duplicate_content: 'Duplicate content',
  order_version: 'Order version',
  new_reader_model_path: 'New reader used model path',
  new_reader_fallback: 'New reader fallback to legacy',
  credit_negative_lines: 'Vendor credit mixed-sign lines',
  scan_header_conflict: 'Two valid readings of date, invoice #, or vendor disagree',
  scan_field_repaired: 'Date or invoice # corrected from the page header',
  amazon_charge_incomplete: 'Amazon: charge not fully reported yet',
  amazon_report_consistency: 'Amazon: report rows disagreed',
  any_flag: 'Any flag',
  no_flags: 'No flags',
}

export function getReviewFlagLabel(flag: string): string {
  if (flag.startsWith('total_validation_mismatch:')) {
    return `Total mismatch: ${flag.split(':')[1]}`
  }
  return FLAG_LABELS[flag] ?? flag
}

const AMOUNT_OP_LABELS: Record<AmountFilterOp, string> = {
  eq: '=',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
}

function formatFilterValue(key: keyof DocReviewFilters, value: string): string {
  if (key === 'doc_type') {
    return DOC_TYPE_LABELS[value] ?? value
  }
  if (key === 'source_type') {
    return SOURCE_LABELS[value] ?? value
  }
  if (key === 'flags') {
    return FLAG_LABELS[value] ?? value
  }
  return value
}

/** Pill adapter for ActiveFilterPillBar (server-driven flat filters, not FilterTree). */
export function generateDocReviewFilterPills(
  filters: DocReviewFilters,
  hasActiveAmountFilter: boolean,
): FilterPill[] {
  const pills: FilterPill[] = []

  for (const key of Object.keys(FIELD_LABELS) as (keyof typeof FIELD_LABELS)[]) {
    if (key === 'amount') {
      if (!hasActiveAmountFilter) continue
      const opLabel = AMOUNT_OP_LABELS[filters.amount_op] ?? filters.amount_op
      pills.push({
        id: 'doc-review-filter-amount',
        nodeId: 'amount',
        label: `${FIELD_LABELS.amount}: ${opLabel} ${filters.amount.trim()}`,
      })
      continue
    }

    const value = filters[key].trim()
    if (!value) continue

    pills.push({
      id: `doc-review-filter-${key}`,
      nodeId: key,
      label: `${FIELD_LABELS[key]}: ${formatFilterValue(key, value)}`,
    })
  }

  return pills
}

export function removeDocReviewFilterPill(
  filters: DocReviewFilters,
  pillId: string,
): DocReviewFilters {
  if (pillId === 'doc-review-filter-amount') {
    return { ...filters, amount: '' }
  }

  const match = pillId.match(/^doc-review-filter-(.+)$/)
  if (!match) return filters

  const key = match[1] as keyof DocReviewFilters
  if (!(key in filters) || key === 'amount_op') return filters

  return { ...filters, [key]: '' }
}
